import {
  getValue,
  getAll,
  fetchAndActivate,
  ensureInitialized,
} from "firebase/remote-config";
import { remoteConfig } from "./init";
import {
  RemoteConfigParameter,
  RemoteConfigValueType,
  UpdateRemoteConfigRequest,
  UpdateRemoteConfigResponse,
} from "@/types/remoteConfig";
import { httpsCallable } from "firebase/functions";
import { getFunctions } from "firebase/functions";
import { app } from "./init";

/**
 * 驗證參數鍵格式
 * Remote Config 參數鍵只能包含字母、數字、底線和連字號
 */
export function validateParameterKey(key: string): { valid: boolean; error?: string } {
  if (!key || key.trim().length === 0) {
    return { valid: false, error: "參數鍵不能為空" };
  }
  
  const trimmedKey = key.trim();
  
  // 檢查長度（Firebase Remote Config 限制）
  if (trimmedKey.length > 256) {
    return { valid: false, error: "參數鍵長度不能超過 256 個字符" };
  }
  
  // 檢查格式：只能包含字母、數字、底線和連字號
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmedKey)) {
    return {
      valid: false,
      error: "參數鍵只能包含字母、數字、底線(_)和連字號(-)",
    };
  }
  
  // 不能以數字開頭
  if (/^\d/.test(trimmedKey)) {
    return { valid: false, error: "參數鍵不能以數字開頭" };
  }
  
  return { valid: true };
}

/**
 * 檢測參數值的類型
 * 注意：這個函數用於從字符串值推斷類型，可能不完全準確
 */
function detectValueType(value: string): RemoteConfigValueType {
  const trimmed = value.trim();
  
  // 檢查是否為 boolean（嚴格匹配 "true" 或 "false"）
  if (trimmed === "true" || trimmed === "false") {
    return "boolean";
  }
  
  // 檢查是否為數字（必須是有效的數字字符串，且不是空字符串）
  // 排除 "NaN", "Infinity" 等特殊值
  if (trimmed !== "" && !isNaN(Number(trimmed)) && isFinite(Number(trimmed))) {
    // 確保不是以 0 開頭的多位數（除非是 "0" 本身）
    if (trimmed === "0" || (trimmed.length > 1 && trimmed[0] !== "0") || trimmed.includes(".")) {
      return "number";
    }
  }
  
  // 預設為字串
  return "string";
}

/**
 * 將值轉換為對應類型
 */
function convertValue(
  value: string,
  type: RemoteConfigValueType
): string | number | boolean {
  switch (type) {
    case "boolean":
      return value === "true";
    case "number":
      return Number(value);
    case "string":
    default:
      return value;
  }
}

/**
 * 獲取所有 Remote Config 參數
 */
export async function getAllParameters(): Promise<RemoteConfigParameter[]> {
  try {
    // 確保 Remote Config 已初始化
    await ensureInitialized(remoteConfig);

    // 獲取所有參數
    const all = getAll(remoteConfig);
    const parameters: RemoteConfigParameter[] = [];

    for (const [key, value] of Object.entries(all)) {
      try {
        const valueType = detectValueType(value.asString());
        const convertedValue = convertValue(value.asString(), valueType);

        parameters.push({
          key,
          value: convertedValue,
          valueType,
          valueSource: value.getSource(),
          description: undefined, // Remote Config 不支援描述，需要從其他地方獲取
        });
      } catch (error) {
        // 跳過無法解析的參數，記錄錯誤但繼續處理其他參數
        console.warn(`Failed to parse parameter ${key}:`, error);
      }
    }

    // 按鍵名排序
    parameters.sort((a, b) => a.key.localeCompare(b.key));

    return parameters;
  } catch (error) {
    console.error("Error fetching Remote Config parameters:", error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "獲取 Remote Config 參數失敗"
    );
  }
}

/**
 * 獲取單一 Remote Config 參數
 */
export async function getParameter(
  key: string
): Promise<RemoteConfigParameter | null> {
  try {
    await ensureInitialized(remoteConfig);

    const value = getValue(remoteConfig, key);
    if (!value) {
      return null;
    }

    const valueType = detectValueType(value.asString());
    const convertedValue = convertValue(value.asString(), valueType);

    return {
      key,
      value: convertedValue,
      valueType,
      valueSource: value.getSource(),
      description: undefined,
    };
  } catch (error) {
    console.error(`Error fetching Remote Config parameter ${key}:`, error);
    throw new Error(
      error instanceof Error
        ? error.message
        : `獲取參數 ${key} 失敗`
    );
  }
}

/**
 * 重新載入 Remote Config（從伺服器獲取最新值）
 */
export async function refreshRemoteConfig(): Promise<void> {
  try {
    await fetchAndActivate(remoteConfig);
  } catch (error) {
    console.error("Error refreshing Remote Config:", error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "重新載入 Remote Config 失敗"
    );
  }
}

/**
 * 更新 Remote Config 參數（透過 Firebase Functions）
 */
export async function updateRemoteConfig(
  request: UpdateRemoteConfigRequest
): Promise<UpdateRemoteConfigResponse> {
  try {
    const functions = getFunctions(app, "asia-east1");
    const updateRemoteConfigFn = httpsCallable<
      UpdateRemoteConfigRequest,
      UpdateRemoteConfigResponse
    >(functions, "updateRemoteConfig");

    const result = await updateRemoteConfigFn(request);
    return result.data;
  } catch (error: any) {
    console.error("Error updating Remote Config:", error);
    throw new Error(
      error.message || "更新 Remote Config 失敗"
    );
  }
}

/**
 * 更新 Remote Config 參數（便利函數）
 */
export async function updateRemoteConfigParams(
  parameters: Record<string, string | number | boolean>,
  deleteKeys?: string[]
): Promise<UpdateRemoteConfigResponse> {
  // 驗證所有參數鍵
  for (const key of Object.keys(parameters)) {
    const validation = validateParameterKey(key);
    if (!validation.valid) {
      throw new Error(`參數鍵 "${key}" 驗證失敗: ${validation.error}`);
    }
  }

  if (deleteKeys) {
    for (const key of deleteKeys) {
      const validation = validateParameterKey(key);
      if (!validation.valid) {
        throw new Error(`要刪除的參數鍵 "${key}" 驗證失敗: ${validation.error}`);
      }
    }
  }

  return updateRemoteConfig({ parameters, deleteKeys });
}

/**
 * 發布 Remote Config 變更（透過 Firebase Functions）
 */
export async function publishRemoteConfig(): Promise<UpdateRemoteConfigResponse> {
  try {
    const functions = getFunctions(app, "asia-east1");
    const publishRemoteConfigFn = httpsCallable<
      void,
      UpdateRemoteConfigResponse
    >(functions, "publishRemoteConfig");

    const result = await publishRemoteConfigFn();
    return result.data;
  } catch (error: any) {
    console.error("Error publishing Remote Config:", error);
    throw new Error(
      error.message || "發布 Remote Config 失敗"
    );
  }
}

